import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RecipeFormData } from '@/types/recipe';
import { Send } from 'lucide-react';
// Modification goes through chatService.confirmModification() so its
// conversation history stays in sync with what the user sees in the UI.
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import NutritionRingsCard from '@/components/studio/NutritionRingsCard';
import QuickTweakButtons from '@/components/studio/QuickTweakButtons';
import { useRecipeStudio } from './studio/RecipeStudioContext';
import { useDietaryAnalysis } from '@/hooks/useDietaryAnalysis';
import { updateUserActivity, estimateTokenUsage, trackTokenUsage } from "@/lib/dashboard-service";
import { useAuth } from '@/contexts/AuthContext';
import { getCostForAction, spendCoins } from '@/services/chefcoinService';
import { writePendingRecipe } from '@/lib/pendingRecipe';
import { getStudioImageJob, clearStudioImageJob } from '@/services/studioImageJob';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { UpgradeModal } from '@/components/common/UpgradeModal';
import {
  StudioChatService,
  createStudioChatService,
} from '@/services/studioChat';
import { chefPersonas } from '@/config/chefPersonas';

// Sub-component for displaying the generated recipe image with loading state
const RecipeImageDisplay = ({ recipe }: { recipe: RecipeFormData }) => {
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoadingError, setImageLoadingError] = useState<string | null>(null);

  const imageUrl = recipe.image_url;
  const isStudioRecipe = !!(recipe as RecipeFormData & { fromStudio?: boolean }).fromStudio;

  useEffect(() => {
    if (isStudioRecipe && !imageUrl) {
      setIsImageLoading(true);
    }

    const handleImageGenerated = () => {
      setIsImageLoading(false);
    };

    const handleImageGenerationFailed = (event: CustomEvent) => {
      setIsImageLoading(false);
      setImageLoadingError(event.detail.error || 'Image generation failed');
    };

    window.addEventListener('studioImageGenerated', handleImageGenerated as EventListener);
    window.addEventListener('studioImageGenerationFailed', handleImageGenerationFailed as EventListener);

    return () => {
      window.removeEventListener('studioImageGenerated', handleImageGenerated as EventListener);
      window.removeEventListener('studioImageGenerationFailed', handleImageGenerationFailed as EventListener);
    };
  }, [imageUrl, isStudioRecipe]);

  if (isImageLoading) {
    return (
      <div className="w-full h-full rounded-xl overflow-hidden bg-gradient-to-br from-orange-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center border-2 border-dashed border-orange-200 dark:border-gray-600">
        <div className="text-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
          <p className="text-sm font-medium text-orange-600 dark:text-orange-400">🎨 Generating image...</p>
        </div>
      </div>
    );
  }

  if (imageLoadingError) {
    return (
      <div className="w-full h-full rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center border border-gray-200 dark:border-gray-700">
        <div className="text-center text-gray-500 dark:text-gray-400 p-4">
          <div className="text-3xl mb-2">🍽️</div>
          <p className="text-sm">Image generation failed</p>
        </div>
      </div>
    );
  }

  if (!recipe.image_url) {
    return (
      <div className="w-full h-full rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        <div className="text-center text-gray-500 dark:text-gray-400 p-4">
          <div className="text-3xl mb-2">🍽️</div>
          <p className="text-sm">Recipe image</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full rounded-xl overflow-hidden">
      <img
        src={recipe.image_url}
        alt={`Generated image of ${recipe.title}`}
        className="w-full h-full object-cover"
      />
    </div>
  );
};

const RecipeReviewPage = () => {
  const navigate = useNavigate();
  const { user, chefcoins_balance, setChefcoinsBalance, isGuest, userProfile } = useAuth();
  const [recipe, setRecipe] = useState<RecipeFormData | null>(null);
  const [messages, setMessages] = useState<{ id: number; text: string; sender: 'user' | 'assistant' }[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { resetStudio } = useRecipeStudio();
  const { assessment, isLoading: isAnalyzing } = useDietaryAnalysis(recipe);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [pendingUserInput, setPendingUserInput] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const chatServiceRef = useRef<StudioChatService | null>(null);
  const [personaId, setPersonaId] = useState<string>('');
  const [modificationPreview, setModificationPreview] = useState<string>('');

  const modificationCost = getCostForAction('RECIPE_MODIFY');

  const currentPersona = chefPersonas.find(p => p.id === personaId);
  const personaAvatar = currentPersona?.avatar || '/chef_ai.png';
  const personaName = currentPersona?.name || 'Chef Assistant';

  const loadRecipeData = () => {
    const studioData = sessionStorage.getItem("studioRecipeData");
    if (studioData) {
      try {
        const parsedRecipe = JSON.parse(studioData) as RecipeFormData & { fromStudio?: boolean; studioPersona?: string };
        setRecipe(parsedRecipe);
        return parsedRecipe;
      } catch {
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    const initializeChat = async () => {
      const initialRecipe = loadRecipeData();

      if (initialRecipe) {
        // Persona resolution: the studio generation always stamps
        // studioPersona onto the recipe. The legacy 'recipeStudioState'
        // sessionStorage key was never written anywhere — its read is gone.
        let loadedPersonaId = 'TheCulinaryAlchemist';

        const recipeWithPersona = initialRecipe as RecipeFormData & { studioPersona?: string };
        if (recipeWithPersona.studioPersona) {
          loadedPersonaId = recipeWithPersona.studioPersona;
        }

        setPersonaId(loadedPersonaId);

        const service = createStudioChatService({
          personaId: loadedPersonaId,
          recipe: initialRecipe
        });

        await service.initialize();
        chatServiceRef.current = service;

        const welcomeMessage = service.getWelcomeMessage();
        setMessages([{
          id: Date.now(),
          text: welcomeMessage,
          sender: 'assistant'
        }]);

        if (user?.id) {
          updateUserActivity(user.id);
        }
      } else {
        navigate('/studio');
      }
    };

    initializeChat();

    const handleImageGenerated = () => {
      loadRecipeData();
    };

    window.addEventListener('studioImageGenerated', handleImageGenerated);

    return () => {
      window.removeEventListener('studioImageGenerated', handleImageGenerated);
    };
  }, [navigate, user?.id]);

  const handleSendMessage = async () => {
    if (!userInput.trim() || !recipe) return;

    if (isGuest()) {
      alert("Please complete your profile to use the AI assistant!");
      return;
    }

    if (!chatServiceRef.current) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await chatServiceRef.current.sendMessage(userInput);

      if (!result.success) {
        setMessages(prev => [...prev,
          { id: Date.now(), text: userInput, sender: 'user' as const },
          { id: Date.now() + 1, text: result.error || "I'm sorry, I couldn't process your message.", sender: 'assistant' as const }
        ]);
        setUserInput('');
        return;
      }

      if (result.requiresConfirmation) {
        if (chefcoins_balance !== null && chefcoins_balance < (result.estimatedCost || modificationCost)) {
          setIsUpgradeModalOpen(true);
          setIsLoading(false);
          return;
        }

        setPendingUserInput(result.pendingModification || userInput);
        setModificationPreview(result.response || '');
        setIsConfirmModalOpen(true);
        setIsLoading(false);
        return;
      }

      const newMessages = [...messages, { id: Date.now(), text: userInput, sender: 'user' as const }];
      setMessages(newMessages);
      setUserInput('');

      if (result.response) {
        setMessages([...newMessages, {
          id: Date.now() + 1,
          text: result.response,
          sender: 'assistant' as const
        }]);
      }

    } catch {
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: "I'm having trouble understanding. Could you please rephrase your question?",
        sender: 'assistant' as const
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmSendMessage = async () => {
    if (!pendingUserInput.trim() || !recipe || !user || !chatServiceRef.current) {
      setIsConfirmModalOpen(false);
      return;
    }

    const newMessages = [...messages, { id: Date.now(), text: pendingUserInput, sender: 'user' as const }];
    setMessages(newMessages);
    const currentInput = pendingUserInput;
    setUserInput('');
    setPendingUserInput('');
    setModificationPreview('');
    setIsLoading(true);
    setIsConfirmModalOpen(false);

    try {
      const spendResult = await spendCoins(user.id, modificationCost, { feature: 'RECIPE_MODIFY' });
      if (!spendResult.success) {
        throw new Error(spendResult.error === 'INSUFFICIENT_FUNDS' ? 'Insufficient ChefCoins' : 'Transaction failed');
      }
      setChefcoinsBalance(spendResult.newBalance);

      // Route through the chat service so its conversation history stays in sync
      const modifiedRecipe = await chatServiceRef.current.confirmModification(
        currentInput,
        { preserveImageUrl: recipe.image_url || undefined }
      );
      if (modifiedRecipe.success && modifiedRecipe.updatedRecipe) {
        const updatedRecipe = modifiedRecipe.updatedRecipe;

        setRecipe(updatedRecipe);

        sessionStorage.setItem('studioRecipeData', JSON.stringify({
          ...updatedRecipe,
          fromStudio: true,
          studioPersona: personaId
        }));

        const assistantResponse = modifiedRecipe.changesSummary || "✅ Recipe updated!";
        setMessages([...newMessages, { id: Date.now() + 1, text: assistantResponse, sender: 'assistant' as const }]);

        if (user?.id) {
          const estimatedTokens = estimateTokenUsage(
            currentInput + JSON.stringify(recipe),
            assistantResponse + JSON.stringify(updatedRecipe)
          );
          trackTokenUsage(user.id, estimatedTokens);
        }
      } else {
        throw new Error(modifiedRecipe.error || 'Unknown error modifying recipe');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "I'm sorry, I encountered an error.";
      setMessages([...newMessages, { id: Date.now() + 1, text: errorMessage, sender: 'assistant' as const }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!recipe || isFinalizing) return;
    setIsFinalizing(true);

    try {
      let finalRecipe = recipe;

      // If the background image job is still in flight, wait for it so the
      // image is not silently discarded (it used to be lost when the user
      // finalized quickly — autoGenerateImage:false blocked regeneration).
      if (!finalRecipe.image_url) {
        const imageJob = getStudioImageJob();
        if (imageJob) {
          try {
            const imageResult = await imageJob;
            if (imageResult.success && imageResult.imageData) {
              finalRecipe = {
                ...finalRecipe,
                image_url: imageResult.imageData,
                has_custom_image: true,
              };
              setRecipe(finalRecipe);
            }
          } catch {
            // Image failed — proceed without it; the form still allows regeneration.
          }
        }
      }

      const finalRecipeData = {
        ...finalRecipe,
        autoGenerateImage: false,
        fromStudio: true
      };
      writePendingRecipe(finalRecipeData);
      clearStudioImageJob();
      navigate('/recipe/new/form?from=studio');
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleStartOver = () => {
    resetStudio();
    sessionStorage.removeItem('studioRecipeData');
    clearStudioImageJob();
    navigate('/studio');
  };

  const handleInputChange = (value: string) => {
    setUserInput(value);
  };

  const handleQuickTweak = (prompt: string) => {
    setUserInput(prompt);
    setIsChatOpen(true);
  };

  if (!recipe) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto h-[calc(100vh-100px)] flex flex-col p-4 md:pb-24">
      {/* ========== DESKTOP LAYOUT ========== */}
      <div className="hidden md:flex flex-col flex-1 min-h-0 gap-4">
        {/* Main Content: Two equal-height cards side by side */}
        <div className="flex-1 grid grid-cols-2 gap-5 min-h-0">

          {/* LEFT CARD: Image + Nutrition */}
          <Card className="flex flex-col bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl rounded-2xl overflow-hidden">
            {/* Hero Image - top section */}
            <div className="flex-shrink-0">
              <div className="aspect-[16/9] overflow-hidden">
                <RecipeImageDisplay recipe={recipe} />
              </div>
            </div>

            {/* Nutrition section - fills remaining space */}
            <div className="flex-1 p-4 overflow-y-auto custom-scrollbar">
              <NutritionRingsCard assessment={assessment} isLoading={isAnalyzing} />
            </div>
          </Card>

          {/* RIGHT CARD: Recipe Content */}
          <Card className="flex flex-col bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl rounded-2xl overflow-hidden">
            {/* Header with title */}
            <CardHeader className="flex-shrink-0 pb-3 border-b border-gray-100 dark:border-gray-700">
              <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">
                {recipe.title}
              </CardTitle>
              {recipe.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                  {recipe.description}
                </p>
              )}
            </CardHeader>

            {/* Scrollable content area */}
            <CardContent className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
              <div className="space-y-5">
                {/* Ingredients */}
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <span className="text-lg">🥗</span> Ingredients
                  </h3>
                  <ReactMarkdown className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
                    {recipe.ingredients}
                  </ReactMarkdown>
                </div>

                {/* Method */}
                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <span className="text-lg">👨‍🍳</span> Method
                  </h3>
                  <ReactMarkdown className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
                    {recipe.method}
                  </ReactMarkdown>
                </div>

                {/* Chef's Notes */}
                {recipe.notes && (
                  <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <span className="text-lg">📝</span> Chef's Notes
                    </h3>
                    <ReactMarkdown className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
                      {recipe.notes}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons - Full width below both cards */}
        <div className="flex-shrink-0 space-y-3">
          {/* Quick Tweak Buttons */}
          <QuickTweakButtons
            onTweak={handleQuickTweak}
            disabled={isLoading}
            personaId={personaId}
          />

          <div className="flex justify-center gap-4">
            <Button
              variant="outline"
              onClick={handleStartOver}
              className="px-6 py-2 font-medium rounded-full border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              ← Start Over
            </Button>
            <Button
              onClick={handleFinalize}
              disabled={isFinalizing}
              className="px-6 py-2 font-medium rounded-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-lg text-white"
            >
              {isFinalizing ? 'Attaching image…' : 'Finalize Recipe →'}
            </Button>
          </div>
        </div>
      </div>

      {/* ========== MOBILE LAYOUT ========== */}
      <div className="md:hidden flex flex-col flex-1 min-h-0 -m-4">
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar pb-20">
          {/* Recipe Image with Title Overlay */}
          <div className="relative">
            <div className="aspect-[16/10]">
              <RecipeImageDisplay recipe={recipe} />
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
              <h1 className="text-xl font-bold text-white drop-shadow-lg">{recipe.title}</h1>
              {recipe.description && (
                <p className="text-white/80 text-xs mt-1 line-clamp-2">{recipe.description}</p>
              )}
            </div>
          </div>

          {/* Nutrition Rings - Compact */}
          <div className="px-4 py-3">
            <NutritionRingsCard assessment={assessment} isLoading={isAnalyzing} />
          </div>

          {/* Recipe Content */}
          <div className="px-4 pb-4 space-y-4">
            {/* Ingredients */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-xl p-4 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">🥗 Ingredients</h3>
              <ReactMarkdown className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                {recipe.ingredients}
              </ReactMarkdown>
            </div>

            {/* Method */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-xl p-4 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">👨‍🍳 Method</h3>
              <ReactMarkdown className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                {recipe.method}
              </ReactMarkdown>
            </div>

            {/* Chef's Notes */}
            {recipe.notes && (
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-xl p-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">📝 Chef's Notes</h3>
                <ReactMarkdown className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  {recipe.notes}
                </ReactMarkdown>
              </div>
            )}

            {/* Quick Tweak Buttons - Mobile */}
            <QuickTweakButtons
              onTweak={handleQuickTweak}
              disabled={isLoading}
              personaId={personaId}
            />
          </div>
        </div>

        {/* FIXED Bottom Action Bar - Mobile */}
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-t border-gray-200 dark:border-gray-700 px-4 py-3 safe-area-pb">
          <div className="flex gap-3 max-w-lg mx-auto">
            <Button
              variant="outline"
              onClick={handleStartOver}
              className="flex-1 rounded-full py-2.5 text-sm font-medium min-h-[44px]"
            >
              ← Start Over
            </Button>
            <Button
              onClick={handleFinalize}
              disabled={isFinalizing}
              className="flex-1 rounded-full py-2.5 text-sm font-medium bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-md text-white min-h-[44px]"
            >
              {isFinalizing ? 'Attaching image…' : 'Finalize Recipe →'}
            </Button>
          </div>
        </div>
      </div>

      {/* ========== CHAT FAB (preserved) ========== */}
      <button
        onClick={() => setIsChatOpen(true)}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 flex items-center gap-2 p-1.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
      >
        <img
          src={recipe?.studioPersona ? chefPersonas.find(p => p.id === recipe.studioPersona)?.avatar : personaAvatar}
          alt="Chef"
          className="w-12 h-12 rounded-full border-2 border-white/30 object-cover"
        />
        <span className="font-medium text-sm hidden md:inline pr-2">Chat with Chef</span>
      </button>

      {/* ========== CHAT MODAL ========== */}
      {isChatOpen && (
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0 bg-black/20" onClick={() => setIsChatOpen(false)} />
          <div className="absolute inset-x-2 bottom-16 md:inset-auto md:bottom-24 md:right-6 md:w-96 max-h-[70vh] md:max-h-[60vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-orange-500 to-pink-500">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={recipe?.studioPersona ? chefPersonas.find(p => p.id === recipe.studioPersona)?.avatar : personaAvatar}
                  alt="Chef"
                  className="w-12 h-12 rounded-full border-2 border-white/60 object-cover shadow-sm flex-shrink-0"
                />
                <div className="flex flex-col min-w-0">
                  <span className="font-semibold text-white text-sm leading-tight truncate">{personaName}</span>
                  {currentPersona?.description && (
                    <span className="text-white/85 text-[11px] leading-tight truncate">{currentPersona.description}</span>
                  )}
                </div>
              </div>
              <button onClick={() => setIsChatOpen(false)} className="text-white/80 hover:text-white p-1 text-lg flex-shrink-0 ml-2">
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-80 bg-gray-50 dark:bg-gray-800/50 custom-scrollbar">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${msg.sender === 'user'
                    ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-br-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-sm shadow-sm'
                    }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-2xl rounded-bl-sm shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-2 bg-white dark:bg-gray-900">
              <input
                type="text"
                value={userInput}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                placeholder="Ask about the recipe..."
                className="flex-1 px-3 py-2 rounded-full bg-gray-100 dark:bg-gray-800 border-0 text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !userInput.trim()}
                className="p-2 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== MODALS ========== */}
      <div className="z-[200]">
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onOpenChange={setIsConfirmModalOpen}
          onConfirm={confirmSendMessage}
          title="Confirm Recipe Modification"
          description={
            <>
              {modificationPreview && (
                <span className="block mb-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                  {modificationPreview}
                </span>
              )}
              <span>This modification will use{' '}<strong className="text-blue-600 dark:text-blue-400">{modificationCost} ChefCoins.</strong></span>
              <br />
              <span>Your new balance will be {chefcoins_balance !== null ? chefcoins_balance - modificationCost : '--'}</span>
            </>
          }
          isLoading={isLoading}
        />
        <UpgradeModal
          isOpen={isUpgradeModalOpen}
          onOpenChange={setIsUpgradeModalOpen}
          featureName="AI Recipe Modification"
          subscription={userProfile?.subscription ?? 'free'}
        />
      </div>

      {/* Custom thin scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.4);
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.6);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.5);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(75, 85, 99, 0.7);
        }
      `}</style>
    </div>
  );
};

export default RecipeReviewPage;